import React, { useMemo, useState, useCallback } from 'react';
import { Calendar as BigCalendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/tr';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { X, Phone, DollarSign, Calendar as CalendarIcon, AlertTriangle, CheckCircle } from 'lucide-react';

moment.locale('tr');
const localizer = momentLocalizer(moment);

const messages = {
  allDay: 'Tüm gün',
  previous: 'Önceki',
  next: 'Sonraki',
  today: 'Bugün',
  month: 'Ay',
  week: 'Hafta',
  day: 'Gün',
  agenda: 'Ajanda',
  date: 'Tarih',
  time: 'Saat',
  event: 'Etkinlik',
  noEventsInRange: 'Bu aralıkta etkinlik yok.',
  showMore: (total) => `+${total} daha`,
};

const formatCurrency = (amount) => {
  if (amount === undefined || amount === null) return '0,00 ₺';
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
};

const calculatePromiseCountdown = (targetDateStr) => {
  if (!targetDateStr) return { daysLeft: null, isOverdue: false };
  
  const targetDate = new Date(targetDateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  targetDate.setHours(0, 0, 0, 0);

  const diffTime = targetDate - today;
  const daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return { daysLeft, isOverdue: daysLeft < 0 };
};

export default function CalendarView({ debtors, getStatusColor, getStatusLabel }) {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentView, setCurrentView] = useState(Views.MONTH);

  // Create events from debtors with payment promise dates
  const events = useMemo(() => {
    return debtors
      .filter(d => d.paymentPromiseDate && !d.isClosed)
      .map(debtor => {
        const promiseDate = new Date(debtor.paymentPromiseDate);
        const { isOverdue } = calculatePromiseCountdown(debtor.paymentPromiseDate);
        
        // Söz tutarı varsa göster, yoksa kalan bakiyeyi göster
        const displayAmount = debtor.paymentPromiseAmount || debtor.balance;
        
        return {
          id: debtor.id,
          title: `${debtor.fullName} - ${formatCurrency(displayAmount)}`,
          start: promiseDate,
          end: promiseDate,
          allDay: true,
          resource: debtor,
          isOverdue: isOverdue
        };
      });
  }, [debtors]);

  // Custom event styling
  const eventStyleGetter = (event) => {
    const { isOverdue } = event;
    const backgroundColor = isOverdue ? '#ef4444' : '#22c55e';
    
    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.9,
        color: 'white',
        border: 'none',
        display: 'block',
        fontWeight: '500',
        fontSize: '12px',
        padding: '2px 6px'
      }
    };
  };

  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event.resource);
  }, []);

  const handleNavigate = useCallback((date) => {
    setCurrentDate(date);
  }, []);

  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
  }, []);

  // Stats
  const stats = useMemo(() => {
    const withPromise = debtors.filter(d => d.paymentPromiseDate && !d.isClosed);
    const overdue = withPromise.filter(d => {
      const { isOverdue } = calculatePromiseCountdown(d.paymentPromiseDate);
      return isOverdue;
    });
    const upcoming = withPromise.filter(d => {
      const { isOverdue } = calculatePromiseCountdown(d.paymentPromiseDate);
      return !isOverdue;
    });
    const totalExpected = withPromise.reduce((acc, d) => acc + (d.balance || 0), 0);
    
    return { total: withPromise.length, overdue: overdue.length, upcoming: upcoming.length, totalExpected };
  }, [debtors]);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <CalendarIcon className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-sm text-slate-500">Toplam Ödeme Sözü</div>
              <div className="text-xl font-bold text-slate-800">{stats.total}</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-sm text-slate-500">Yaklaşan</div>
              <div className="text-xl font-bold text-green-600">{stats.upcoming}</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-sm text-slate-500">Geciken</div>
              <div className="text-xl font-bold text-red-600">{stats.overdue}</div>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <div className="text-sm text-slate-500">Beklenen Tahsilat</div>
              <div className="text-lg font-bold text-slate-800">{formatCurrency(stats.totalExpected)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div style={{ height: 600 }}>
          <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            messages={messages}
            eventPropGetter={eventStyleGetter}
            onSelectEvent={handleSelectEvent}
            onNavigate={handleNavigate}
            onView={handleViewChange}
            date={currentDate}
            view={currentView}
            views={[Views.MONTH, Views.WEEK, Views.AGENDA]}
            defaultView={Views.MONTH}
            popup
            toolbar={true}
            navigable={true}
          />
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <h3 className="font-semibold text-slate-700 mb-3">Renk Açıklamaları</h3>
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-green-500"></div>
            <span className="text-sm text-slate-600">Yaklaşan Ödeme Sözü</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-red-500"></div>
            <span className="text-sm text-slate-600">Geciken Ödeme Sözü</span>
          </div>
        </div>
      </div>

      {/* Event Detail Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center">
              <h3 className="font-bold flex items-center gap-2">
                <CalendarIcon size={18} className="text-amber-500" />
                Ödeme Sözü Detayı
              </h3>
              <button onClick={() => setSelectedEvent(null)} className="hover:text-amber-500">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
                <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center">
                  <span className="font-bold text-slate-600">
                    {selectedEvent.fullName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </span>
                </div>
                <div>
                  <div className="font-bold text-slate-800">{selectedEvent.fullName}</div>
                  <div className="text-sm text-slate-500">{selectedEvent.fileNumber}</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Telefon</div>
                  <div className="font-medium text-slate-700 flex items-center gap-1">
                    <Phone size={14} /> {selectedEvent.phone || '-'}
                  </div>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg">
                  <div className="text-xs text-slate-500 mb-1">Durum</div>
                  <span 
                    className="inline-block px-2 py-1 rounded text-xs font-bold text-white"
                    style={{ backgroundColor: getStatusColor(selectedEvent.status) }}
                  >
                    {getStatusLabel(selectedEvent.status)}
                  </span>
                </div>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg border border-amber-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-amber-800">Ödeme Sözü Tarihi</span>
                  {(() => {
                    const { daysLeft, isOverdue } = calculatePromiseCountdown(selectedEvent.paymentPromiseDate);
                    return (
                      <span className={`text-xs font-bold px-2 py-1 rounded ${isOverdue ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                        {isOverdue ? `${Math.abs(daysLeft)} gün gecikti` : `${daysLeft} gün kaldı`}
                      </span>
                    );
                  })()}
                </div>
                <div className="text-xl font-bold text-amber-900">
                  {new Date(selectedEvent.paymentPromiseDate).toLocaleDateString('tr-TR', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>
                {selectedEvent.paymentPromiseAmount && (
                  <div className="mt-2 pt-2 border-t border-amber-200">
                    <div className="text-sm text-amber-700">Söz Verilen Tutar</div>
                    <div className="text-2xl font-bold text-amber-900">{formatCurrency(selectedEvent.paymentPromiseAmount)}</div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                  <div className="text-xs text-green-600 mb-1">Kalan Borç</div>
                  <div className="font-bold text-green-700">{formatCurrency(selectedEvent.balance)}</div>
                </div>
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                  <div className="text-xs text-blue-600 mb-1">Toplam Tahsilat</div>
                  <div className="font-bold text-blue-700">{formatCurrency(selectedEvent.totalPaid)}</div>
                </div>
              </div>

              <button
                onClick={() => setSelectedEvent(null)}
                className="w-full bg-slate-900 text-white py-3 rounded-lg font-medium hover:bg-slate-800 transition-colors"
              >
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
